import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, Send, Paperclip, Mic, Search, Plus,
  Image, FileText, X, Loader2, Check, CheckCheck,
  Hash, Users, Briefcase, Calendar, Star, Settings
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const CHANNELS = [
  { id: 'general', label: 'General', icon: Hash, description: 'General updates and announcements' },
  { id: 'content', label: 'Content', icon: Image, description: 'Content planning and approvals' },
  { id: 'partnerships', label: 'Partnerships', icon: Briefcase, description: 'Partnership discussions' },
  { id: 'media', label: 'Media & Press', icon: Users, description: 'Media opportunities' },
  { id: 'events', label: 'Events', icon: Calendar, description: 'Event coordination' },
  { id: 'community', label: 'Community', icon: Star, description: 'Community initiatives' }
];

const AEMessages = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [activeChannel, setActiveChannel] = useState('general');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (userEmail) {
      fetchMessages();
    }
  }, [userEmail, activeChannel]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/always-elevate/messages?user_email=${encodeURIComponent(userEmail)}&channel=${activeChannel}`);
      const data = await res.json();
      if (data.success) {
        setMessages(data.data);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/api/always-elevate/messages?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: activeChannel,
          body: newMessage.trim(),
          attachments: []
        })
      });
      const data = await res.json();
      if (data.success) {
        setNewMessage('');
        fetchMessages();
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const activeChannelInfo = CHANNELS.find(c => c.id === activeChannel);
  const ChannelIcon = activeChannelInfo?.icon || Hash;

  const isMe = (email) => email?.toLowerCase() === userEmail?.toLowerCase();

  return (
    <div className="min-h-screen bg-black text-white flex">
      {/* Sidebar - Channels */}
      <div className="w-64 border-r border-white/10 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <p className="text-[#C4A052] text-xs font-medium tracking-wider">ALWAYS ELEVATE</p>
          <h1 className="text-xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }}>Messages</h1>
        </div>

        {/* Search */}
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
            />
          </div>
        </div>

        {/* Channels List */}
        <div className="flex-1 overflow-y-auto p-2">
          <p className="text-xs text-gray-500 uppercase tracking-wider px-2 py-2">Channels</p>
          {CHANNELS.map(channel => {
            const Icon = channel.icon;
            const isActive = activeChannel === channel.id;
            return (
              <button
                key={channel.id}
                onClick={() => setActiveChannel(channel.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  isActive 
                    ? 'bg-[#C4A052]/20 text-[#C4A052]' 
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{channel.label}</span>
              </button>
            );
          })}
        </div>

        {/* Direct Messages */}
        <div className="p-2 border-t border-white/10">
          <p className="text-xs text-gray-500 uppercase tracking-wider px-2 py-2">Direct Messages</p>
          <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <div className="w-8 h-8 rounded-full bg-[#C4A052]/20 flex items-center justify-center text-[#C4A052] text-sm font-bold">
              EM
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">Elle Maxwell</p>
              <p className="text-xs text-gray-500 truncate">Brand Manager</p>
            </div>
          </button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Channel Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/5">
              <ChannelIcon className="w-5 h-5 text-[#C4A052]" />
            </div>
            <div>
              <h2 className="font-semibold">{activeChannelInfo?.label}</h2>
              <p className="text-xs text-gray-400">{activeChannelInfo?.description}</p>
            </div>
          </div>
          <button className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <Settings className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageSquare className="w-16 h-16 text-gray-700 mb-4" />
              <h3 className="text-lg font-medium text-gray-400 mb-2">No messages yet</h3>
              <p className="text-gray-500 text-sm">Start the conversation in #{activeChannelInfo?.label}</p>
            </div>
          ) : (
            messages.map((msg, idx) => {
              const isMine = isMe(msg.sender_email);
              const showAvatar = idx === 0 || messages[idx - 1]?.sender_email !== msg.sender_email;
              
              return (
                <div key={msg.id || idx} className={`flex gap-3 ${isMine ? 'flex-row-reverse' : ''}`}>
                  {showAvatar ? (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      isMine ? 'bg-[#C4A052]/20 text-[#C4A052]' : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {isMine ? 'AE' : 'EM'}
                    </div>
                  ) : (
                    <div className="w-8 flex-shrink-0" />
                  )}
                  
                  <div className={`max-w-[70%] ${isMine ? 'text-right' : ''}`}>
                    {showAvatar && (
                      <div className={`flex items-center gap-2 mb-1 ${isMine ? 'flex-row-reverse' : ''}`}>
                        <span className="text-sm font-medium">
                          {isMine ? 'You' : 'Elle Maxwell'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatTime(msg.created_at)}
                        </span>
                      </div>
                    )}
                    <div className={`rounded-2xl px-4 py-2 ${
                      isMine 
                        ? 'bg-[#C4A052] text-black rounded-tr-md' 
                        : 'bg-white/10 text-white rounded-tl-md'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                    </div>
                    
                    {/* Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {msg.attachments.map((att, i) => (
                          <div key={i} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 text-sm">
                            <Paperclip className="w-4 h-4 text-gray-400" />
                            <span>{att}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-end gap-3">
            <button className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400">
              <Paperclip className="w-5 h-5" />
            </button>
            
            <div className="flex-1 relative">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Message #${activeChannelInfo?.label.toLowerCase()}...`}
                rows={1}
                className="w-full bg-black border border-white/20 rounded-xl px-4 py-3 pr-12 text-sm resize-none focus:outline-none focus:border-[#C4A052] min-h-[48px] max-h-32"
                style={{ height: 'auto' }}
              />
            </div>
            
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="p-3 rounded-xl bg-[#C4A052] text-black hover:bg-[#D4B062] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AEMessages;
